/**
 * Post-auth intent — what the visitor was TRYING to do when we sent them to /auth.
 *
 * WHY THIS EXISTS (board GndWruiohOoiLebkSPok)
 * ---------------------------------------------
 * A logged-out visitor lands on a paid-ad LP, clicks "Start free trial", and is
 * sent to /auth?tab=signup. Every post-auth redirect site called
 * `getPostLoginRedirect(domain)`, which returned '/dashboard' unconditionally.
 * The visitor therefore created an account and landed on a generic dashboard
 * having never started the trial they clicked for — the only success event the
 * ad spend buys. Nothing errored; the funnel just ended.
 *
 * TWO PROPERTIES ARE BOTH REQUIRED, and neither is sufficient alone:
 *   1. returnTo — put them back where they were, and
 *   2. action   — carry the INTENT, so the trial actually starts on arrival.
 * A returnTo alone drops them back on the LP with the same button to click
 * again; an action alone gives them nowhere to land.
 *
 * WHY localStorage AND NOT A `?returnTo=` QUERY PARAM
 * ---------------------------------------------------
 * The signup path is NOT a single hop. `signUp()` creates the account
 * server-side and creates no client session (src/lib/auth.tsx), so AuthForm
 * shows EmailConfirmationPage. The user then leaves for their email client,
 * follows a Firebase action link into /auth/callback, and AuthCallback does a
 * HARD `window.location.href = '/auth?tab=signin'` — dropping every query param
 * and all React state. A URL param cannot survive that; localStorage can.
 *
 * It also removes the open-redirect surface entirely: nothing here is ever
 * populated from a URL, and `returnTo` is validated against a literal
 * allowlist on BOTH write and read, so the only values this module can ever
 * hand back are the string literals in RETURN_TO_ALLOWLIST. An attacker
 * cannot steer a post-login redirect off-origin even by writing storage
 * directly.
 *
 * SCOPED BY CONCEPT, NOT BY PAGE (.claude/rules/propagate-the-fix.md): both
 * NextGen trial LPs have the identical dead-end shape, so this is one
 * "post-auth intent" mechanism rather than a /bar/practice/trial special case.
 */

const STORAGE_KEY = 'jds_post_auth_intent';

/**
 * How long an intent stays live.
 *
 * Must outlast the email-verification round trip (sign up → check email →
 * click link → sign in), which is unbounded in principle and commonly minutes
 * to hours.
 *
 * ── WHY THIS IS 7 DAYS AND NOT 24 HOURS ─────────────────────────────────────
 * It was 24h until 2026-09-11, on the reasoning quoted below. That reasoning is
 * withdrawn, because it weighed the wrong two outcomes against each other:
 *
 *   "It must NOT be effectively permanent: the bar_package trial is
 *    once-per-user-forever, so an intent that fires days later silently spends
 *    something the user cannot get back. 24h is the compromise."
 *
 * Both halves of that are true and the conclusion still does not follow. An
 * intent that fires late spends a trial the visitor ASKED FOR by clicking
 * "Start free trial" — they get the thing they came for, a few days later than
 * they expected. An intent that EXPIRES spends the trial too, in the sense that
 * matters: `peekPostAuthIntent` returns null, `getPostLoginRedirect`
 * (src/lib/auth-utils.ts) falls through to '/dashboard', and the visitor lands
 * SIGNED IN, holding NO entitlement, on a page they never asked for, with
 * nothing anywhere pointing back at the offer. There is no error and no
 * recovery path. So the expiry does not protect the trial — it destroys it, and
 * destroys a paid ad click with it.
 *
 * The window has to cover how people actually process email: a Friday-evening
 * signup opened on Sunday, a verification mail found in spam the next day. 24h
 * clears neither. 7 days does, and is still far from permanent — a visitor who
 * returns three weeks later gets '/dashboard', which is the pre-intent
 * behaviour rather than a new failure.
 *
 * NOTHING HERE GRANTS ANYTHING. The intent is a return path plus a verb, both
 * allowlisted on write AND on read (see RETURN_TO_ALLOWLIST below); it is not a
 * credential and cannot be steered off-origin. The entitlement itself is still
 * gated server-side by /pay/start-trial, whose pre-check plus
 * `ON CONFLICT (user_id, feature) DO NOTHING`
 * (services/payment/src/routes/trial.ts) is what actually enforces
 * once-per-user-forever. A longer TTL cannot produce a second trial.
 *
 * Pinned by execution in src/lib/__tests__/auth-intent-ttl.test.ts, which
 * advances fake timers past 24h (still resolves) and past 7 days (expires).
 */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The only paths a post-auth redirect may target. EXACT string membership —
 * not a prefix test, not a regex, not "starts with /". Every LP that sets an
 * intent is listed here; adding a surface means adding its literal path.
 *
 * `/lp/nextgen-practice-a` differs from the other two in HOW it uses the entry.
 * The others send a logged-out visitor to /auth and come back. That page signs
 * the visitor up IN PLACE, so its round trip is longer and passes through a
 * surface neither of the others touches: signUp() creates no client session, so
 * the visitor leaves for their email client, returns through /auth/callback,
 * and AuthCallback hard-navigates to `/auth?tab=signin` — where they must enter
 * their password before `getPostLoginRedirect` is ever consulted. The intent has
 * to survive all of that, which is exactly why it lives in localStorage rather
 * than a query param (see the header). Verified against
 * src/pages/auth/AuthCallback.tsx:52.
 */
const RETURN_TO_ALLOWLIST = [
  '/bar/practice/trial',
  // REPLACED '/nextgen-prep' ON 2026-09-11 WITH THE 301, AND THE SUBSTITUTION
  // FIXED A LIVE NO-OP. BarPracticePage has called
  // `setPostAuthIntent({ returnTo: '/bar/practice' })` since 2026-09-08, but
  // `setPostAuthIntent` takes `returnTo: string` and SILENTLY RETURNS when the
  // value is not allowlisted (see below) — so every logged-out CTA click on the
  // new product hub recorded no intent at all, and the visitor landed on
  // /dashboard instead of back on the page with their trial started. tsc could
  // not see it: the parameter is deliberately loose so a caller cannot smuggle
  // an arbitrary destination in, which means a WRONG destination fails open.
  // (.claude/rules/silent-degradation.md — a guard that degrades to "do
  // nothing" reads as success.) Enrolling the path is the whole fix.
  '/bar/practice',
  '/lp/nextgen-practice-a',
] as const;

export type PostAuthReturnTo = (typeof RETURN_TO_ALLOWLIST)[number];

/**
 * What to DO on arrival. Today there is exactly one: start the NextGen
 * (`bar_package`) 7-day trial. A trial for any other feature would be a
 * distinct action, never a parameter on this one — features have separate
 * entitlement rows and separate eligibility.
 */
export type PostAuthAction = 'start_bar_trial';

export interface PostAuthIntent {
  returnTo: PostAuthReturnTo;
  action: PostAuthAction;
  /** Epoch ms. Used only for the TTL check. */
  createdAt: number;
}

function isAllowedReturnTo(value: unknown): value is PostAuthReturnTo {
  return (
    typeof value === 'string' &&
    (RETURN_TO_ALLOWLIST as readonly string[]).includes(value)
  );
}

function isKnownAction(value: unknown): value is PostAuthAction {
  return value === 'start_bar_trial';
}

/**
 * Every storage access is wrapped: `localStorage` THROWS (not returns null) in
 * Safari private mode and when a browser is set to block site data. An intent
 * we cannot persist must degrade to the old '/dashboard' behavior, never to a
 * crashed auth form.
 */
function readRaw(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remove the stored intent. Safe to call when nothing is stored. */
export function clearPostAuthIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Record what the visitor was trying to do, immediately before navigating them
 * to /auth. Silently no-ops if `returnTo` is not allowlisted — a caller cannot
 * smuggle an arbitrary destination in.
 */
export function setPostAuthIntent(intent: {
  returnTo: string;
  action: PostAuthAction;
}): void {
  if (!isAllowedReturnTo(intent.returnTo) || !isKnownAction(intent.action)) return;
  try {
    const payload: PostAuthIntent = {
      returnTo: intent.returnTo,
      action: intent.action,
      createdAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable — the flow degrades to the pre-fix behavior */
  }
}

/**
 * Read the stored intent WITHOUT consuming it. Returns null (and clears the
 * record) when it is absent, unparseable, malformed, or past its TTL.
 *
 * Non-destructive on purpose: `getPostLoginRedirect` peeks, and it is called
 * from redirect effects that can run several times (AuthForm's
 * already-authenticated effect has its own loop counter for exactly that
 * reason). Consuming there would destroy the intent before the destination
 * page ever mounts. The destination consumes.
 */
export function peekPostAuthIntent(): PostAuthIntent | null {
  const raw = readRaw();
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPostAuthIntent();
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    clearPostAuthIntent();
    return null;
  }

  const candidate = parsed as Record<string, unknown>;
  if (
    !isAllowedReturnTo(candidate.returnTo) ||
    !isKnownAction(candidate.action) ||
    typeof candidate.createdAt !== 'number' ||
    !Number.isFinite(candidate.createdAt)
  ) {
    clearPostAuthIntent();
    return null;
  }

  if (Date.now() - candidate.createdAt > TTL_MS) {
    clearPostAuthIntent();
    return null;
  }

  return {
    returnTo: candidate.returnTo,
    action: candidate.action,
    createdAt: candidate.createdAt,
  };
}

/**
 * Read AND remove the intent in one step. Callers that are about to act on an
 * intent MUST use this rather than peek-then-clear, and must call it BEFORE
 * any `await` — that ordering is what makes a refresh, a double-click, or a
 * StrictMode double-effect unable to fire the same intent twice.
 */
export function consumePostAuthIntent(): PostAuthIntent | null {
  const intent = peekPostAuthIntent();
  clearPostAuthIntent();
  return intent;
}
